import { apiGet, apiRequest } from "./client.js";
import { fetchEveryPage } from "./collection.js";
import {
  parseDecisions,
  parseFeed,
  parseFeeds,
  parseHistory,
  parseNowPlaying,
} from "./parse.js";
import type { Airing, Decision, Feed, NowPlaying } from "./types.js";

/**
 * Everything this app asks of a feed. The two public reads are open to anyone; the
 * rest need the operator's bearer, and they are the same routes a machine with a
 * token would call — there is no private endpoint here and no privileged read.
 */

const feedPath = (feedId: string, suffix = "") =>
  `/api/feeds/${encodeURIComponent(feedId)}${suffix}`;

export async function fetchNowPlaying(
  feedId: string,
  signal?: AbortSignal,
): Promise<NowPlaying> {
  return parseNowPlaying(
    await apiGet<unknown>(feedPath(feedId, "/now-playing"), { signal }),
  );
}

export async function fetchHistory(
  feedId: string,
  limit = 12,
  signal?: AbortSignal,
): Promise<Airing[]> {
  const path = feedPath(feedId, `/history?limit=${String(limit)}`);
  return parseHistory(await apiGet<unknown>(path, { signal }));
}

/**
 * Every feed, enabled or not: a disabled feed is still one of the operator's. The
 * route pages, and the console counts what it shows, so this follows the cursor to
 * the end rather than reporting the first page as if it were the set.
 */
export async function fetchFeeds(signal?: AbortSignal): Promise<Feed[]> {
  return fetchEveryPage("/api/feeds", parseFeeds, { signal });
}

export async function fetchFeed(
  feedId: string,
  signal?: AbortSignal,
): Promise<Feed | null> {
  return parseFeed(await apiGet<unknown>(feedPath(feedId), { signal }));
}

export async function fetchNextUp(
  feedId: string,
  signal?: AbortSignal,
): Promise<Decision[]> {
  return parseDecisions(
    await apiGet<unknown>(feedPath(feedId, "/next-up"), { signal }),
  );
}

/**
 * What a new feed is made of. `default_playlist_id` is required by the contract and
 * not by this form's taste: a feed that references no playlist has nothing it could
 * play. Everything else the request leaves unnamed is the schema's own baseline.
 */
export interface FeedCreateInput {
  name: string;
  description?: string;
  default_playlist_id: string;
  schedule_id?: string | null;
  fallback_playlist_id?: string | null;
  crossfade_ms?: number;
  enabled?: boolean;
}

export interface FeedPatchInput {
  name?: string;
  description?: string;
  schedule_id?: string | null;
  default_playlist_id?: string;
  fallback_playlist_id?: string | null;
  crossfade_ms?: number;
  loudness_target_lufs?: number;
  true_peak_ceiling_dbtp?: number;
  loudness_range_lu?: number;
  enabled?: boolean;
}

export async function createFeed(input: FeedCreateInput): Promise<Feed | null> {
  return parseFeed(
    await apiRequest<unknown>("/api/feeds", { method: "POST", body: input }),
  );
}

export async function patchFeed(
  feedId: string,
  input: FeedPatchInput,
): Promise<Feed | null> {
  return parseFeed(
    await apiRequest<unknown>(feedPath(feedId), {
      method: "PATCH",
      body: input,
    }),
  );
}

export async function deleteFeed(feedId: string): Promise<void> {
  await apiRequest<void>(feedPath(feedId), { method: "DELETE" });
}

/** Addressed to one feed's own engine instance. Skipping one disturbs no other. */
export async function skipFeed(feedId: string): Promise<void> {
  await apiRequest<unknown>(feedPath(feedId, "/skip"), { method: "POST" });
}

export async function restartFeed(feedId: string): Promise<void> {
  await apiRequest<unknown>(feedPath(feedId, "/restart"), { method: "POST" });
}
